<template>
  <page-view :is-loading="loading">
    <template #header>
      <base-header :title="$t('message.player')" :info="$t('message.settings')">
        <template #actions>
          <base-btn
            :disabled="loadingButtonOriginalSettings"
            type="secondary"
            @click="modalConfirmationOriginalSettings = true"
            v-if="hasEditVideoPerm"
          >
            {{ $t('message.playerPage.backOriginalSettings') }}
          </base-btn>
          <base-btn :disabled="loadingButtonSave" @click="handleEditPlayer" v-if="hasEditVideoPerm">
            {{ $t('message.playerPage.save') }}
          </base-btn>
        </template>
      </base-header>
    </template>

    <template #content>
      <div class="grid">
        <!-- embed -->
        <div class="grid__preview">
          <div class="grid__preview--container" v-html="embedPage" />
        </div>

        <div class="grid__control">
          <!-- settings::controls -->
          <div>
            <h3 class="player-subtitle">
              {{ $t('message.playerPage.controls') }}
            </h3>
            <div class="grid__control--items">
              <template v-for="(item, index) in controlsItems">
                <v-switch
                  v-model="controls"
                  :key="`control-item-${index}`"
                  class="mt-0"
                  :label="item.label"
                  :value="item.value"
                  @click="unsavedChanges = true"
                />
              </template>
            </div>
          </div>

          <!-- settings::language -->
          <div class="mb-6">
            <h3 class="player-subtitle">
              {{ $t('message.playerPage.language') }}
            </h3>
            <v-select
              v-model="language"
              :items="languages"
              :menu-props="{ top: false, offsetY: true }"
              item-text="name"
              item-value="value"
              :label="$t('message.playerPage.languageLabel')"
              outlined
              dense
              hide-details
              @change="unsavedChanges = true"
            />
          </div>

          <!-- settings::colors -->
          <div class="mb-6">
            <h3 class="player-subtitle">
              {{ $t('message.playerPage.playerColors') }}
            </h3>
            <v-row no-gutters>
              <v-col class="d-flex align-center">
                <v-menu
                  v-model="menuControlsColor"
                  :close-on-content-click="false"
                  offset-x
                  nudge-bottom="105"
                  nudge-left="16"
                  top
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      :color="controlsColor"
                      :style="setControlsColor"
                      elevation="3"
                      icon
                    />
                  </template>
                  <v-color-picker
                    v-model="controlsColor"
                    :maxlength="max"
                    elevation="3"
                    mode="hexa"
                    hide-mode-switch
                    flat
                    @input="changePlayer"
                    @keydown="changeColors()"
                  />
                </v-menu>
                <p class="mb-0 ml-3">
                  {{ $t('message.playerPage.controlBarColor') }}
                </p>
              </v-col>
              <v-col class="d-flex align-center">
                <v-menu
                  v-model="menuPrimaryColor"
                  :close-on-content-click="false"
                  offset-x
                  nudge-bottom="105"
                  nudge-left="16"
                  top
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      :color="primaryColor"
                      :style="setPrimaryColor"
                      elevation="3"
                      icon
                    />
                  </template>
                  <v-color-picker
                    elevation="3"
                    v-model="primaryColor"
                    flat
                    mode="hexa"
                    hide-mode-switch
                    @keydown="changeColors()"
                    :maxlength="max"
                    @input="changePlayer"
                  />
                </v-menu>
                <p class="mb-0 ml-3">
                  {{ $t('message.playerPage.primaryColor') }}
                </p>
              </v-col>
            </v-row>
          </div>

          <!-- settings::subtitlesColors -->
          <div class="mb-6">
            <h3 class="player-subtitle">
              {{ $t('message.playerPage.subtitleColor') }}
            </h3>
            <v-row no-gutters>
              <v-col class="d-flex align-center">
                <v-menu
                  v-model="subtitlesColor.menuFg"
                  :close-on-content-click="false"
                  offset-x
                  nudge-bottom="105"
                  nudge-left="16"
                  top
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      :color="subtitlesColor.foreground"
                      :style="setSubtitlesFontColor"
                      elevation="3"
                      icon
                    />
                  </template>
                  <v-color-picker
                    v-model="subtitlesColor.foreground"
                    :maxlength="max"
                    elevation="3"
                    mode="hexa"
                    hide-mode-switch
                    flat
                    @input="changePlayer"
                    @keydown="changeColors()"
                  />
                </v-menu>
                <p class="mb-0 ml-3">
                  {{ $t('message.playerPage.subtitleFontColor') }}
                </p>
              </v-col>
              <v-col class="d-flex align-center">
                <v-menu
                  v-model="subtitlesColor.menuBg"
                  :close-on-content-click="false"
                  offset-x
                  nudge-bottom="105"
                  nudge-left="16"
                  top
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      :color="subtitlesColor.background"
                      :style="setSubtitlesBgColor"
                      elevation="3"
                      icon
                    />
                  </template>
                  <v-color-picker
                    elevation="3"
                    v-model="subtitlesColor.background"
                    flat
                    mode="hexa"
                    hide-mode-switch
                    @keydown="changeColors()"
                    :maxlength="max"
                    @input="changePlayer"
                  />
                </v-menu>
                <p class="mb-0 ml-3">
                  {{ $t('message.playerPage.subtitleBackgroundColor') }}
                </p>
              </v-col>
            </v-row>
          </div>

          <!-- settings::subtitleSize -->
          <div class="mb-6">
            <h3 class="player-subtitle">
              {{ $t('message.playerPage.subtitleFontSize') }}
            </h3>
            <v-row no-gutters>
              <v-col class="d-flex align-center">
                <v-select
                  v-model="subtitleFontSize"
                  hide-details
                  :items="fontSizes"
                  :label="
                    $t('message.pages.videoEditModal.fontSizeSettingsLabel')
                  "
                  item-text="label"
                  :menu-props="{ top: false, offsetY: true }"
                  outlined
                  dense
                  @change="changePlayer"
                ></v-select>
              </v-col>
            </v-row>
          </div>

          <!-- settings::marketing -->
          <div class="mb-6">
            <h3 class="player-subtitle">
              {{ $t('message.playerPage.marketing') }}
            </h3>
            <v-row no-gutters>
              <v-col>
                <v-text-field
                  v-model="fbPixel"
                  :label="$t('message.playerPage.facebookPixel')"
                  :placeholder="$t('message.playerPage.facebookPixel')"
                  class="mt-0"
                  v-mask="`################`"
                  prepend-inner-icon="mdi-facebook"
                  type="text"
                  dense
                  outlined
                  hide-details
                  @keydown="setFbPixel"
                />
              </v-col>
              <v-col cols="auto" class="d-flex align-center pl-2">
                <v-btn
                  :href="facebookPixelLink"
                  color="primary"
                  target="_blank"
                  icon
                >
                  <v-icon>mdi-help-circle</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row class="mt-4" no-gutters>
              <v-col>
                <v-text-field
                  v-model="gtagIds"
                  :label="$t('message.playerPage.googlePixel')"
                  :placeholder="
                    $t(
                      'message.pages.videoPlayerPixels.placeholders.id.gtagIds'
                    )
                  "
                  class="mt-0"
                  prepend-inner-icon="mdi-google"
                  type="text"
                  dense
                  outlined
                  hide-details
                  @keydown="setGtagIds"
                />
              </v-col>
              <v-col cols="auto" class="d-flex align-center pl-2">
                <v-btn
                  :href="googlePixelLink"
                  color="primary"
                  target="_blank"
                  icon
                >
                  <v-icon>mdi-help-circle</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <!-- settings::affiliate -->
          <div class="mb-6">
            <h3 class="player-subtitle">
              {{ $t('message.playerPage.messageAffiliateCode') }}
            </h3>
            <v-row no-gutters>
              <v-col>
                <v-text-field
                  v-model="refCode"
                  :disabled="formDisable"
                  :label="$t('message.playerPage.affiliateCode')"
                  :placeholder="$t('message.playerPage.affiliateCode')"
                  class="mt-0"
                  prepend-inner-icon="mdi-currency-usd"
                  type="text"
                  dense
                  outlined
                  hide-details
                  @keydown="setRefCode"
                  @keydown.space.prevent
                />
              </v-col>
              <v-col cols="auto" class="d-flex align-center pl-2">
                <v-btn
                  icon
                  color="primary"
                  href="https://afiliados.pandavideo.com.br/"
                  target="_blank"
                >
                  <v-icon>mdi-help-circle</v-icon>
                </v-btn>
              </v-col>
              <v-col cols="12" class="mt-3">
                <v-switch
                  v-model="pandaBranding"
                  :label="$t('message.pages.video.controlsPage.showLogo')"
                  class="mt-0 pt-0"
                  hide-details
                  @click="
                    ;(unsavedChanges = true), (formDisable = !formDisable)
                  "
                />
              </v-col>
            </v-row>
          </div>

          <!-- settings::save-progress -->
          <div>
            <h3 class="player-subtitle">
              {{ $t('message.playerPage.others') }}
            </h3>
            <v-row no-gutters class="pt-0">
              <v-col cols="auto">
                <v-switch
                  v-model="saveProgress"
                  :label="$t('message.pages.video.saveProgress')"
                  class="mt-0"
                  width="auto"
                  hide-details
                  @click="unsavedChanges = true"
                />
              </v-col>
              <v-col class="d-flex align-center pl-2">
                <v-btn
                  :href="saveVideoProgressLink"
                  icon
                  color="primary"
                  target="_blank"
                >
                  <v-icon>mdi-help-circle</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>
      </div>

      <v-dialog
        v-model="modalConfirmationChanges"
        persistent
        max-width="600px"
        hide-overlay
        @keydown="handleEvent"
      >
        <v-card dense>
          <v-toolbar elevation="0">
            <v-toolbar-title class="pl-2 text--subtitle">
              <v-icon color="primary">mdi-play-circle-outline</v-icon>
              {{ $t('message.playerPage.exitWithoutSaving') }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn icon @click="modalConfirmationChanges = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-card-text class="pl-7 pr-5 py-0">
            <v-row no-gutters>
              <v-col cols="12">
                <v-card-text class="pa-0">{{
                  $t('message.playerPage.textConfirmChanges')
                }}</v-card-text>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="px-8 pt-4 pb-6">
            <v-spacer></v-spacer>
            <v-btn
              class="mr-2 px-4"
              text
              @click="modalConfirmationChanges = false"
            >
              {{ $t('message.playerPage.back') }}
            </v-btn>
            <v-btn
              color="primary"
              @click=";(modalConfirmationChanges = false), discardChanges()"
            >
              {{ $t('message.playerPage.discardChanges') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="modalConfirmationOriginalSettings"
        persistent
        width="600px"
        hide-overlay
        @keydown="handleEvent"
      >
        <v-card dense>
          <v-toolbar elevation="0">
            <v-toolbar-title class="pl-2 text--subtitle">
              <v-icon color="primary">mdi-play-circle-outline</v-icon>
              {{ $t('message.playerPage.exitOriginalSettings') }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn icon @click="modalConfirmationOriginalSettings = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-card-text class="pl-7 pr-5 py-0">
            <v-row no-gutters>
              <v-col cols="12">
                <v-card-text class="pa-0">{{
                  $t('message.playerPage.textConfirmOriginalSettings')
                }}</v-card-text>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="px-8 pt-4 pb-6">
            <v-spacer></v-spacer>
            <v-btn
              class="mr-2 px-4"
              text
              @click="modalConfirmationOriginalSettings = false"
            >
              {{ $t('message.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              @click="
                ;(modalConfirmationOriginalSettings = false),
                  handleOriginalSettings()
              "
            >
              <span class="px-5">{{
                $t('message.playerPage.confirmOriginalSettings')
              }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </page-view>
</template>

<script>
// external imports
import { mask } from 'vue-the-mask'
import { mapState } from 'vuex'

// internal imports
import playerApi from '@/services/playerApi'
import profileApi from '@/services/profileApi'
import linkHelper from '@/utils/linkHelper.mixin.js'
import i18n from '@/translations/lang'

// components
import PageView from '@/layout/PageView.vue'

export default {
  name: 'Player',
  directives: { mask },
  components: {
    PageView,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
    BaseBtn: () => import('@/components/BaseBtn.vue'),
  },
  mixins: [linkHelper],
  beforeRouteLeave(to, from, next) {
    if (this.checkChanges() && this.unsavedChanges) {
      this.modalConfirmationChanges = true
      this.to = to
    } else {
      next()
    }
  },
  data: () => ({
    subtitleFontSize: 'normal',
    subtitlesColor: {
      menuFg: false,
      menuBg: false,
      foreground: '#ffffff',
      background: '#000000',
    },
    video: {
      videoURL:
        'https://b-vz-ded14ebd-85a.tv.pandavideo.com.br/d39148d9-9aa1-4a1e-98a9-4535812e95fe/playlist.m3u8',
      thumb:
        'https://thumbs.tv.pandavideo.com.br/vz-ded14ebd-85a/3b101f05-84aa-4de0-9b64-71f1855388af.png',
    },
    initialPlayer: {
      playerControlsColor: '',
      playerPrimaryColor: '',
      playerControls: [],
      playerSaveProgress: true,
      playerFbPixel: '',
      playerGtagIds: '',
      playerRefCode: '',
      defaultLanguage: '',
    },
    controlsColor: '#000000',
    menuControlsColor: false,
    primaryColor: '#000000',
    menuPrimaryColor: false,
    controls: [],
    saveProgress: true,
    fbPixel: '',
    gaPixel: '',
    refCode: '',
    pandaBranding: true,
    loading: false,
    loadingButtonSave: false,
    loadingButtonOriginalSettings: false,
    modalConfirmationChanges: false,
    modalConfirmationOriginalSettings: false,
    unsavedChanges: null,
    to: null,
    max: 7,
    formDisable: false,
    arrayProfiles: [],
    profile: '',
    toggle: true,
    language: '',
    pixelFontSizes: {
      small: 14,
      normal: 20,
      large: 36,
    },
    facebookArticleLinks: {
      pt: 'https://help.pandavideo.com/pt-br/article/id-do-facebook-pixel-pj2pyc/',
      en: 'https://help.pandavideo.com/en-us/article/facebook-pixel-id-1hhdur6/',
    },
    googleArticleLinks: {
      pt: 'https://help.pandavideo.com/pt-br/article/id-do-google-pixel-tf89jl/',
      en: 'https://help.pandavideo.com/en-us/article/google-pixel-id-1wxl9ja/',
    },
  }),
  computed: {
    ...mapState({
      userPlan: (state) => state.user.userPlanInfo,
    }),
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    fontSizes() {
      return [
        {
          label: i18n.t('message.pages.videoEditModal.fontSizes.small'),
          value: 'small',
        },
        {
          label: i18n.t('message.pages.videoEditModal.fontSizes.normal'),
          value: 'normal',
        },
        {
          label: i18n.t('message.pages.videoEditModal.fontSizes.large'),
          value: 'large',
        },
      ]
    },
    embedPage() {
      return `<div style="position:relative;padding-top:56.25%;"><iframe id="panda-player" src="https://player-vz-ded14ebd-85a.tv.pandavideo.com.br/embed/?v=3b101f05-84aa-4de0-9b64-71f1855388af&alternativeProgress=false&mutedIndicatorIcon=false&controls=${this.organizeControls()}${
        this.pandaBranding ? '&pandaBranding=true' : '&pandaBranding=false'
      }${
        this.language ? '&defaultLanguage=' + this.language : ''
      }" style="border:none;position:absolute;top:0;height:100%;width:100%" allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"allowfullscreen=true></iframe></div>`
    },
    saveVideoProgressLink() {
      return this.getSaveVideoProgressLink()
    },
    facebookPixelLink() {
      const articleLink =
        this.facebookArticleLinks[this.$i18next.resolvedLanguage] ??
        this.facebookArticleLinks['en']
      return articleLink
    },
    googlePixelLink() {
      const articleLink =
        this.googleArticleLinks[this.$i18next.resolvedLanguage] ??
        this.googleArticleLinks['en']
      return articleLink
    },
    setControlsColor() {
      const { controlsColor, menuControlsColor } = this
      let root = document.querySelector(':root')
      root.style.setProperty('--plyr-video-control-color', controlsColor)
      return {
        backgroundColor: controlsColor,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: menuControlsColor ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setSubtitlesFontColor() {
      let root = document.querySelector(':root')
      root.style.setProperty(
        '--plyr-color-main',
        this.subtitlesColor.foreground
      )
      return {
        backgroundColor: this.subtitlesColor.foreground,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: this.subtitlesColor.menuFg ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setSubtitlesBgColor() {
      let root = document.querySelector(':root')
      root.style.setProperty(
        '--plyr-color-main',
        this.subtitlesColor.background
      )
      return {
        backgroundColor: this.subtitlesColor.background,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: this.subtitlesColor.menuBg ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setPrimaryColor() {
      const { menuPrimaryColor, primaryColor } = this
      let root = document.querySelector(':root')
      root.style.setProperty('--plyr-color-main', primaryColor)
      return {
        backgroundColor: primaryColor,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: menuPrimaryColor ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    controlsItems() {
      return [
        {
          label: 'Big Play Button',
          value: 'play-large',
        },
        {
          label: 'Play/Pause',
          value: 'play',
        },
        {
          label: i18n.t('message.pages.video.controlsPage.progress'),
          value: 'progress',
        },
        {
          label: i18n.t('message.pages.video.controlsPage.currentTime'),
          value: 'current-time',
        },
        {
          label: 'Volume',
          value: 'volume',
        },
        {
          label: i18n.t('message.pages.video.controlsPage.captions'),
          value: 'captions',
        },
        {
          label: i18n.t('message.pages.video.controlsPage.settings'),
          value: 'settings',
        },
        {
          label: 'Fullscreen',
          value: 'fullscreen',
        },
        {
          label: 'Picture-in-Picture',
          value: 'pip',
        },
        {
          label: i18n.t('message.pages.video.controlsPage.forward'),
          value: 'fast-forward',
        },
        {
          label: i18n.t('message.pages.video.controlsPage.backward'),
          value: 'rewind',
        },
        {
          label: 'Airplay',
          value: 'airplay',
        },
        {
          label: 'Chromecast',
          value: 'cast',
        },
        // {
        //   label: i18n.t('message.pages.video.controlsPage.smartSearch'),
        //   value: 'smartSearch',
        // },
      ]
    },
    languages() {
      return [
        {
          value: '',
          name: this.$i18next.t('message.automatic'),
        },
        {
          value: 'pt',
          name: this.$i18next.t('message.portuguese'),
        },
        {
          value: 'en',
          name: this.$i18next.t('message.english'),
        },
        {
          value: 'es',
          name: this.$i18next.t('message.spanish'),
        },
      ]
    },
  },
  async created() {
    await this.readPlayer()
    await this.readProfile()
  },
  mounted() {
    window.addEventListener('message', (event) => {
      if (!this.profile.colors) return
      const pandaPlayer = document.getElementById('panda-player')

      if (pandaPlayer) {
        const iframe = pandaPlayer.contentWindow
        const { data } = event
        const { captionsColor, captionsBackgroundColor } = this.profile.colors

        if (data.message === 'panda_ready') {
          iframe.postMessage(
            {
              type: 'colors',
              parameter: {
                colors: {
                  primaryColor: this.primaryColor,
                  controlsColor: this.controlsColor,
                  captionsColor: captionsColor,
                  captionsBackgroundColor: captionsBackgroundColor,
                },
              },
            },
            '*'
          )
        }
      }
    })
  },
  methods: {
    async readPlayer() {
      this.loading = true
      try {
        const ret = await playerApi.getPlayer(this.listQuery)
        this.loading = false
        this.subtitlesColor.foreground = ret.data.config.captionsColor.substr(
          0,
          7
        )
        this.subtitlesColor.background =
          ret.data.config.captionsBackgroundColor.substr(0, 7)
        this.subtitleFontSize = this.getFontSizeObject(
          ret.data.config.captionsFontSize
        )
        this.controlsColor = ret.data.config.controlsColor
        this.saveProgress = ret.data.config.saveProgress
        this.fbPixel = ret.data.config.fbPixel
        this.gtagIds = ret.data.config.gtagIds ? ret.data.config.gtagIds[0] : ''
        this.refCode = ret.data.config.refCode
        this.pandaBranding = ret.data.config.pandaBranding
        this.primaryColor = ret.data.config.primaryColor
        this.controls = ret.data.config.controls
        this.language = ret.data.config.defaultLanguage || ''
        this.initialPlayer = {
          playerControlsColor: ret.data.config.controlsColor,
          playerPrimaryColor: ret.data.config.primaryColor,
          playerControls: ret.data.config.controls,
          playerSaveProgress: ret.data.config.saveProgress,
          playerFbPixel: ret.data.config.fbPixel,
          playerGtagIds: ret.data.config.gtagIds
            ? ret.data.config.gtagIds[0]
            : '',
          playerRefCode: ret.data.config.refCode,
          pandaBranding: ret.data.config.pandaBranding,
          defaultLanguage: ret.data.config.defaultLanguage,
        }
        this.formDisable = !this.pandaBranding
        this.unsavedChanges = false
        return ret
      } catch (e) {
        console.log(e)
        this.loading = false
      }
    },
    async readProfile() {
      try {
        const ret = await profileApi.getProfile()
        this.arrayProfiles = ret.data.map((element) => element.name)
        this.arrayProfiles.push('+ Novo Profile')
      } catch (e) {
        console.log(e)
      }
    },
    async handleEditPlayer() {
      this.loadingButtonSave = true
      let controlsToApi = this.controls
      const organizedControls = [
        'play-large',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        'volume',
        'captions',
        'settings',
        'pip',
        'fullscreen',
        'airplay',
        'cast',
        'smartSearch',
      ]
      controlsToApi.sort(function (a, b) {
        return organizedControls.indexOf(a) - organizedControls.indexOf(b)
      })
      const regexAffiliateCode = this.getAffiliateCodeFromURL(
        'ref',
        this.refCode
      )
      try {
        const apiPlayer = {
          controls: controlsToApi,
          primaryColor: this.primaryColor,
          controlsColor: this.controlsColor,
          saveProgress: this.saveProgress,
          captionsColor: this.subtitlesColor.foreground,
          captionsBackgroundColor: this.subtitlesColor.background,
          captionsFontSize: this.pixelFontSizes[this.subtitleFontSize],
          pandaBranding: this.pandaBranding,
          fbPixel: this.unmask(this.fbPixel),
          gtagIds: [this.gtagIds],
          refCode: regexAffiliateCode,
          defaultLanguage: this.language,
        }
        await playerApi.editPlayer({ ...apiPlayer })
      } catch (e) {
        console.log(e)
      }
      this.unsavedChanges = false
      this.loadingButtonSave = false
    },
    getFontSizeObject(size) {
      size = Number(size)
      if (size < 20) {
        return this.fontSizes[0].value
      } else if (size === 20 || !size) {
        return this.fontSizes[1].value
      }
      return this.fontSizes[2].value
    },
    unmask(text) {
      const defaultDelimiters = /[-!$%^&*()_+|~=`{}[\]:";'<>?,./\\ ]/
      return text
        ? String(text).replace(new RegExp(defaultDelimiters, 'g'), '')
        : text
    },
    changePlayer() {
      if (!this.hasEditVideoPerm) return
      const iframe = document.getElementById('panda-player').contentWindow
      iframe.postMessage(
        {
          type: 'colors',
          parameter: {
            colors: {
              primaryColor: this.primaryColor,
              controlsColor: this.controlsColor,
              captionsColor: this.subtitlesColor.foreground,
              captionsBackgroundColor: this.subtitlesColor.background,
            },
          },
        },
        '*'
      )
      return
    },
    setFbPixel() {
      this.unsavedChanges = true
    },
    setGtagIds() {
      this.unsavedChanges = true
    },
    setRefCode() {
      this.unsavedChanges = true
    },
    getAffiliateCodeFromURL(parameterName, url) {
      if (!url) return ''
      parameterName = parameterName.replace(/[[\]]/g, '\\><')
      let regularExpressionGetRef = new RegExp(
          '[?&]' + parameterName + '(=([^&#]*)|&|#|$)'
        ),
        resultGetRef = regularExpressionGetRef.exec(url)
      if (!resultGetRef) return this.refCode
      if (!resultGetRef[2]) return ''
      return decodeURIComponent(resultGetRef[2].replace(/\+/g, ''))
    },
    checkChanges() {
      const playerChanged = {
        playerControls: this.controls,
        playerPrimaryColor: this.primaryColor,
        playerControlsColor: this.controlsColor,
        playerSaveProgress: this.saveProgress,
        playerFbPixel: this.fbPixel,
        playerGtagIds: this.gtagIds,
        playerRefCode: this.refCode,
        pandaBranding: this.pandaBranding,
        defaultLanguage: this.language,
      }
      const array1 = playerChanged.playerControls
      const array2 = this.initialPlayer.playerControls
      const is_same =
        array1.length == array2.length &&
        array1.every(function (element) {
          if (array2.indexOf(element) > -1) {
            return (element = array2[array2.indexOf(element)])
          }
        })
      return is_same &&
        playerChanged.playerPrimaryColor ==
          this.initialPlayer.playerPrimaryColor &&
        playerChanged.playerControlsColor ==
          this.initialPlayer.playerControlsColor &&
        playerChanged.playerSaveProgress ==
          this.initialPlayer.playerSaveProgress &&
        playerChanged.playerFbPixel == this.initialPlayer.playerFbPixel &&
        playerChanged.playerGtagIds == this.initialPlayer.playerGtagIds &&
        playerChanged.playerRefCode == this.initialPlayer.playerRefCode &&
        playerChanged.defaultLanguage == this.initialPlayer.defaultLanguage &&
        playerChanged.pandaBranding == this.initialPlayer.pandaBranding
        ? false
        : true
    },
    handleOriginalSettings() {
      this.loadingButtonOriginalSettings = true
      this.controls = [
        'play-large',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        'volume',
        'captions',
        'settings',
        'pip',
        'fullscreen',
        'airplay',
        'cast',
        'smartSearch',
      ]
      this.primaryColor = '#4874F1'
      this.controlsColor = '#FFFFFF'
      this.saveProgress = true
      this.pandaBranding = true
      this.loadingButtonOriginalSettings = false
      this.language = this.$i18next.resolvedLanguage
    },
    discardChanges() {
      this.unsavedChanges = false
      this.$router.push({ name: this.to.name })
    },
    handleClose() {
      this.modalConfirmationChanges = false
      this.modalConfirmationOriginalSettings = false
    },
    changeColors() {
      this.unsavedChanges = true
    },
    handleEvent(event) {
      const e = event
      if (e.key) {
        e.key === 'Escape' && this.handleClose()
      }
      return
    },
    organizeControls() {
      let controlsToApi = this.controls
      const organizedControls = [
        'play-large',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        'volume',
        'captions',
        'settings',
        'pip',
        'fullscreen',
        'airplay',
        'cast',
        'smartSearch',
      ]
      controlsToApi.sort(function (a, b) {
        return organizedControls.indexOf(a) - organizedControls.indexOf(b)
      })
      return this.controls
    },
  },
}
</script>

<style lang="scss" scoped>
:root {
  --plyr-color-main: #fafafa;
  --plyr-captions-text-color: #fafafa;
  --plyr-captions-background: #fafafa;
  --plyr-video-control-color: #fafafa;
  --plyr-captions-text-color: #fafafa;
}

.player-subtitle {
  margin-bottom: 8px;

  font-weight: normal;
  color: var(--v-neutral-base);

  + * {
    padding-top: 6px;
  }
}

.grid {
  display: grid;
  grid-template-areas: 'preview' 'control';
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  column-gap: 1rem;
  row-gap: 2rem;

  box-shadow: none !important;

  @media (min-width: 1200px) {
    grid-template-areas: 'preview control';
    grid-template-columns: 50% auto;
    grid-template-rows: auto;
  }

  &__preview {
    grid-area: preview;

    &--container {
      margin: 0 auto;
      width: 75vw;

      @media (min-width: 760px) {
        width: 50vw;
      }

      @media (min-width: 1200px) {
        width: 100%;
      }
    }
  }

  &__control {
    grid-area: control;

    padding-left: 1rem;
    padding-right: 1rem;
    overflow-y: auto;
    overflow-x: hidden;

    &--items {
      display: grid;
      padding-top: 0 !important;

      grid-template-columns: 1fr 1fr;

      color: var(--v-neutral-base);
    }
  }
}

.bg-dark {
  .player-subtitle {
    color: var(--v-neutral-lighten2);
  }
}
</style>
